import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';

import { ChatSessionKey } from 'flow/types/EntityKeys';
import ThreadMessageList from './ThreadMessageList';
import ThreadParentMessage from './ThreadParentMessage';
import { GenericCanvasTransition } from '../communication-canvas/CSSTransition';
import {
    clearSelectedThreadMessage,
    getThreadChatMessages,
} from 'flow/actions/chat-actions';
import ThreadMessagePage from './ThreadMessagePage';
import ChatMessage from 'flow/pages/messages/ChatMessage';
import MessageInput from 'flow/pages/messages/MessageInput';
import { MessageThreadCheckProvider } from 'flow/contexts/MessageThreadCheckProvider';

interface Props {
    showVideoCanvas: boolean;
    setShowVideoCanvas: (show: boolean) => void;
    showCanvas: boolean;
}

interface ThreadListMethods {
    updateMessageSend: () => void;
}

const ThreadMessageWrapper: React.FC<Props> = ({
    showVideoCanvas,
    showCanvas,
    setShowVideoCanvas,
}) => {
    const { i18next, showDialpad, setShowDialpad } = useAppContext();
    const dispatch = useAppDispatch<any>();
    const threadListRef = useRef<ThreadListMethods>(null);

    const [resizeChange, setResizeChange] = useState(false);

    const selectedThreadMessage = useAppSelector((state) =>
        state.chats.get('threadMessage'),
    );

    const messageKey = useMemo(
        () => selectedThreadMessage?.getIn(['currentThread', 'chatMessageKey']),
        [selectedThreadMessage],
    );

    const sessionKey: ChatSessionKey = useMemo(
        () => selectedThreadMessage?.getIn(['currentThread', 'chatSessionKey']),
        [selectedThreadMessage],
    );

    useEffect(() => {
        if (showCanvas || showDialpad || showVideoCanvas) {
            dispatch(clearSelectedThreadMessage());
        }
    }, [showCanvas, showDialpad, showVideoCanvas, dispatch]);

    useEffect(() => {
        if (messageKey) {
            setShowVideoCanvas(false);
            setShowDialpad(false);
        }
    }, [messageKey, dispatch]);

    useEffect(() => {
        if (sessionKey && messageKey) {
            dispatch(getThreadChatMessages(sessionKey, messageKey, null));
        }
    }, [sessionKey, messageKey]);

    const sessionMessages = useAppSelector((state) =>
        state.chats.getIn(['messages', sessionKey]),
    );

    const currentMessage = useMemo(
        () =>
            sessionMessages?.find(
                (message) => message.get('key') === messageKey,
            ),
        [sessionMessages, messageKey],
    );

    const messageCount = useMemo(
        () => currentMessage?.getIn(['threadSummary', 'messageCount']),
        [currentMessage],
    );

    const onInputResize = () => setResizeChange(!resizeChange);

    return (
        <GenericCanvasTransition
            transitionIn={!!messageKey}
            timeout={{
                enter: 300,
                exit: 300,
            }}
        >
            {messageKey ? (
                <ThreadMessagePage
                    header={
                        currentMessage?.get('message') ||
                        i18next.t('ThreadMessages.DefaultTitle')
                    }
                    mainChatMessage={
                        <MessageThreadCheckProvider value={true}>
                            <ThreadParentMessage>
                                <ChatMessage
                                    sessionKey={sessionKey}
                                    showAvatar={true}
                                    message={currentMessage}
                                    cbOpenGraph={() => {}}
                                    showReactions={false}
                                    showContextMenu={false}
                                    showThreadMessageSummary={false}
                                    messageLineClamp={5}
                                />
                            </ThreadParentMessage>
                        </MessageThreadCheckProvider>
                    }
                    messageCount={messageCount}
                    threadList={
                        <ThreadMessageList
                            register={(methods) => {
                                threadListRef.current = methods;
                            }}
                            messageKey={messageKey}
                            sessionKey={sessionKey}
                        />
                    }
                    footer={
                        <MessageInput
                            chatSession={sessionKey}
                            onInputResize={onInputResize}
                            threadMessageKey={messageKey}
                            afterSend={() => {
                                if (threadListRef.current) {
                                    threadListRef.current.updateMessageSend();
                                }
                            }}
                        />
                    }
                />
            ) : (
                <div />
            )}
        </GenericCanvasTransition>
    );
};

export default ThreadMessageWrapper;
